import React, { useState } from 'react';

import { Typography, Chip, Button } from '@material-ui/core';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import { useTranslation } from 'react-i18next';

import RichTextContent from 'components/RichTextContent/RichTextContent';
import type { ActionTemplate, ActionTemplateStep } from 'config/api/actionLibrary/actionLibrary.types';
import general_messages from 'messages/general_messages';

import useStyles from './ActionTemplateDetailsSteps.styles';

type StepItemProps = {
  step: ActionTemplateStep;
  index: number;
};

const StepItem: React.FC<StepItemProps> = ({ step, index }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const [expanded, setExpanded] = useState(false);

  return (
    <div style={{ marginBottom: 16, padding: 16, border: '1px solid #e0e0e0', borderRadius: 4 }}>
      <Typography style={{ marginBottom: 8 }} variant='h6'>
        {index + 1}. {step.title}
        {step.mandatory && <Chip className={styles.mandatoryChip} label='Mandatory' size='small' variant='outlined' />}
      </Typography>
      <Typography component='div' style={{ marginBottom: 8 }} variant='body2'>
        <div className={!expanded ? styles.clampedText : ''}>
          <RichTextContent content={step.shortDescription} />
        </div>
        <div className={styles.readMoreContainer}>
          <Button
            className={styles.readMoreButton}
            onClick={() => setExpanded(!expanded)}
            size='small'
            startIcon={expanded ? <VisibilityOffIcon /> : <VisibilityIcon />}
          >
            {expanded ? t(general_messages.hide) : t(general_messages.show_more)}
          </Button>
        </div>
      </Typography>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
        {step.reference && <Chip label={`Reference: ${step.reference}`} size='small' variant='outlined' />}
        {step.relevantFor && <Chip label={`Relevant For: ${step.relevantFor}`} size='small' variant='outlined' />}
      </div>
      {step.tags && typeof step.tags === 'object' && Object.keys(step.tags).length > 0 && (
        <div style={{ marginTop: 8 }}>
          <Typography style={{ display: 'block', marginBottom: 4 }} variant='caption'>
            Tags:
          </Typography>
          <div className={styles.tagsContainer}>
            {Object.entries(step.tags).map(([key, value]) => (
              <Chip key={key} label={`${key}: ${String(value)}`} size='small' />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

type Props = {
  steps: ActionTemplate['steps'];
};

const ActionTemplateDetailsSteps: React.FC<Props> = ({ steps }) => {
  if (!steps) return null;

  return (
    <>
      {steps
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map((step, index) => (
          <StepItem key={step.id} index={index} step={step} />
        ))}
    </>
  );
};

export default ActionTemplateDetailsSteps;
